/**
 * Shell for the unauthenticated screens: sign in, forgot and reset password.
 *
 * Redirects an already-signed-in visitor away, so hitting /login with a live
 * session lands on the dashboard rather than on a sign-in form.
 *
 * Two halves on a wide screen: NexERP's identity — the apps you are about to
 * walk into — and the form. On a narrow one the form stands alone.
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext.jsx';
import { LoadingState } from '../components/States.jsx';
import { APPS, appName } from './apps.js';
import { AppIcon, BrandMark } from './Brand.jsx';

function Wordmark({ size = 28 }) {
  return (
    <span className="auth-hero-brand">
      <BrandMark size={size} />
      <span className="brand-word">
        Nex<span className="brand-word-strong">ERP</span>
      </span>
    </span>
  );
}

/** The brand side of the sign-in screens. */
export function AuthHero() {
  const { t } = useTranslation('navigation');
  return (
    <aside className="auth-hero" aria-hidden="true">
      <Wordmark />
      <div className="auth-hero-copy">
        <h2 className="auth-hero-title">
          <Trans t={t} i18nKey="auth.heroTitle" components={{ accent: <span /> }} />
        </h2>
        <p className="auth-hero-text">{t('auth.heroText')}</p>
        <div className="auth-apps">
          {APPS.map((app, index) => (
            <span key={app.key} className="auth-app" style={{ animationDelay: `${120 + index * 45}ms` }}>
              <AppIcon app={app} size="sm" />
              {appName(app, t)}
            </span>
          ))}
        </div>
      </div>
      <p className="auth-hero-foot">{t('auth.heroFoot')}</p>
    </aside>
  );
}

export function AuthLayout() {
  const { t } = useTranslation('navigation');
  const { status, isAuthenticated } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <LoadingState label={t('common:states.loading')} />;

  if (isAuthenticated) {
    const destination = location.state?.from?.pathname ?? '/';
    return <Navigate to={destination} replace />;
  }

  return (
    <div className="auth-shell">
      <AuthHero />
      <main className="auth-panel">
        <div className="auth-card">
          <div className="auth-brand auth-brand-mobile">
            <BrandMark size={30} />
            <span className="brand-word">
              Nex<span className="brand-word-strong">ERP</span>
            </span>
          </div>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
